import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { SettingsWell } from '@/components/shared/settings/SettingsKit'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import { verify } from '@/lib/api/api'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon } from '@hugeicons/core-free-icons'
import EmbedCodeDisplay from './EmbedCodeDisplay'

interface InstallTabProps {
  domain: string
  propertyId?: string
}

export default function InstallTab({ domain }: InstallTabProps) {
  const { token, user } = useAuth()
  const scriptId = user?.scriptId
  const [isVerifying, setIsVerifying] = useState(false)
  const [lastVerifiedOk, setLastVerifiedOk] = useState<boolean | null>(null)

  const handleVerifyInstallation = async () => {
    if (!scriptId || !token) {
      toast.error('Unable to verify installation')
      return
    }

    try {
      setIsVerifying(true)
      const result = await verify(domain, scriptId, token)
      if (result.scriptInstalledHead) {
        setLastVerifiedOk(true)
        toast.success('Script is correctly installed in the <head> tag')
      } else {
        setLastVerifiedOk(false)
        if (result.scriptInstalled) {
          toast.error('Script found, but not in the <head> tag')
        } else {
          toast.error('Tracking script not detected yet')
        }
      }
    } catch {
      setLastVerifiedOk(false)
      toast.error('Verification failed')
    } finally {
      setIsVerifying(false)
    }
  }

  const status =
    lastVerifiedOk === true
      ? { dot: 'bg-live', title: 'Script found in the head', body: `Verified just now on ${domain}.` }
      : lastVerifiedOk === false
        ? { dot: 'bg-warning', title: 'Script not detected yet', body: `Paste the code below into the head of ${domain}, publish, then verify again.` }
        : { dot: 'bg-foreground/30', title: 'Install the tracking script', body: `Paste the code below into the head of ${domain}, then verify.` }

  if (!scriptId) {
    return (
      <SettingsWell>
        <section className="surface-raised flex flex-col gap-1 px-[18px] py-4">
          <h2 className="text-sm font-semibold leading-[18px] text-foreground">No script ID yet</h2>
          <p className="text-xs leading-4 text-foreground/60">
            Refresh the page or re-add the site to generate a tracking snippet.
          </p>
        </section>
      </SettingsWell>
    )
  }

  return (
    <SettingsWell>
      <section className="surface-raised flex items-center gap-2.5 px-[18px] py-4">
        <span aria-hidden className={cn('mt-1.5 size-1.5 shrink-0 self-start rounded-full', status.dot)} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm font-semibold leading-[18px] text-foreground">{status.title}</p>
          <p className="text-xs leading-4 text-foreground/60">{status.body}</p>
        </div>
        <Button
          variant="outline"
          onClick={handleVerifyInstallation}
          disabled={isVerifying}
          className="h-[34px] shrink-0 gap-1.5 rounded-lg px-3 text-xs font-medium shadow-none"
        >
          {isVerifying && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
          {isVerifying ? 'Verifying…' : 'Verify install'}
        </Button>
      </section>
      <EmbedCodeDisplay scriptId={scriptId} />
    </SettingsWell>
  )
}
